import {
	ArrowLongLeftIcon,
	ArrowLongRightIcon,
} from '@heroicons/react/24/outline'

import audioFile from '../../media/impressed.mp3'
import {
	STORAGE_KEY as NOTIFICATION_SOUND_KEY,
	SOUND_CHANGED_EVENT,
} from './NotificationSoundPicker'
import { getSound } from '../../utils/soundDb'

export function colorBasedOnAge(lastUpdatedAt) {
	const baseStyle = {
		textAlign: 'center',
		borderRadius: '3px',
		width: '40px',
		height: '25px',
		fontSize: '14px', // Adjust the font size as needed
	}

	if (lastUpdatedAt === undefined) {
		return {
			...baseStyle,
			color: '#28a358',
			backgroundColor: '#e4f6e7',
			width: '75px',
		}
	}

	const timeSinceUpdate = Date.now() - new Date(lastUpdatedAt)
	const hoursSinceUpdate = timeSinceUpdate / 1000 / 60 / 60

	if (hoursSinceUpdate < 4) {
		return {
			...baseStyle,
			color: '#28a358',
			backgroundColor: '#e4f6e7',
		}
	} else if (hoursSinceUpdate < 24) {
		return {
			...baseStyle,
			color: '#dc8132',
			backgroundColor: '#fee9c8',
		}
	} else {
		return {
			...baseStyle,
			color: '#5a6278',
			backgroundColor: '#e2e5ea',
		}
	}
}

export function colorBasedOnAgeSimilarLoads(lastUpdatedAt) {
	const baseStyle = {
		textAlign: 'center',
		borderRadius: '3px',
		width: '35px',
		height: '20px',
		fontSize: '14px', // Adjust the font size as needed
	}

	if (lastUpdatedAt === undefined) {
		return {
			...baseStyle,
			color: '#28a358',
			backgroundColor: '#e4f6e7',
			width: '75px',
		}
	}

	const timeSinceUpdate = Date.now() - new Date(lastUpdatedAt)
	const hoursSinceUpdate = timeSinceUpdate / 1000 / 60 / 60

	if (hoursSinceUpdate < 4) {
		return {
			...baseStyle,
			color: '#28a358',
			backgroundColor: '#e4f6e7',
		}
	} else if (hoursSinceUpdate < 24) {
		return {
			...baseStyle,
			color: '#dc8132',
			backgroundColor: '#fee9c8',
		}
	} else {
		return {
			...baseStyle,
			color: '#5a6278',
			backgroundColor: '#e2e5ea',
		}
	}
}

export function formatLastSeenDate(date) {
	if (date === undefined) return 'N/A'

	const dateArg = new Date(date)

	const now = new Date()
	const diff = now.getTime() - dateArg.getTime()
	const seconds = Math.floor(diff / 1000)
	const minutes = Math.floor(seconds / 60)
	const hours = Math.floor(minutes / 60)
	const days = Math.floor(hours / 24)
	const months = Math.floor(days / 30)

	if (seconds < 60) {
		return '1m'
	} else if (minutes < 60) {
		return minutes + 'm'
	} else if (hours < 24) {
		return hours + 'hr'
	} else if (days < 30) {
		return days + 'd'
	} else {
		return months + 'mo'
	}
}

export const ArrowDirection = ({ load }) => {
	const firstCity = `${load.pickup.address.city}, ${load.pickup.address.state}`
	const lastCity = `${load.dropOff.address.city}, ${load.dropOff.address.state}`

	const isRoundTrip = firstCity === lastCity

	return isRoundTrip ? (
		<span className="flex justify-center items-center">
			<ArrowLongLeftIcon className="h-5 w-5 -mr-2" />
			<ArrowLongRightIcon className="h-5 w-5" />
		</span>
	) : (
		<span className="flex justify-center items-center">
			<ArrowLongRightIcon className="h-5 w-5" />
		</span>
	)
}

export function validateTimeZone(timeZone, dayjs) {
	try {
		if (dayjs.tz.zone(timeZone)) {
			return timeZone
		}
		throw new Error('Invalid time zone')
	} catch (e) {
		console.error('Invalid time zone specified:', timeZone)
		return 'America/New_York'
	}
}

export function dateDifference(date1, date2) {
	const startDate = new Date(date1)
	const endDate = new Date(date2)

	const diffInMilliseconds = endDate - startDate

	const hours = Math.floor(diffInMilliseconds / (1000 * 60 * 60))

	if (hours < 24) {
		return `${hours}hrs`
	}

	const days = Math.floor(hours / 24)
	const remainingHours = hours % 24

	return `${days}d ${remainingHours}hrs`
}

export function truncateString(cityName, maxLength = 14) {
	if (!cityName) return ''

	// If the string is only 2 characters longer than the cut length,
	// show it as is instead of truncating
	if (cityName.length <= maxLength + 2) {
		return cityName
	}

	return cityName.length > maxLength
		? cityName.substr(0, maxLength) + '...'
		: cityName
}

let audio = new Audio(audioFile)
let currentObjectUrl = null

function releaseObjectUrl() {
	if (currentObjectUrl) {
		URL.revokeObjectURL(currentObjectUrl)
		currentObjectUrl = null
	}
}

async function rebuildAudio() {
	try {
		const raw = localStorage.getItem(NOTIFICATION_SOUND_KEY)
		const saved = raw ? JSON.parse(raw) : null

		if (saved?.url) {
			const next = new Audio(saved.url)
			next.load()
			audio = next
			releaseObjectUrl()
			return
		}

		if (saved?.id) {
			const stored = await getSound(saved.id)
			if (stored?.blob?.size) {
				const url = URL.createObjectURL(stored.blob)
				const next = new Audio(url)
				next.load()
				audio = next
				releaseObjectUrl()
				currentObjectUrl = url
				return
			}
		}
	} catch {}
	audio = new Audio(audioFile)
	releaseObjectUrl()
}

rebuildAudio()

if (typeof window !== 'undefined') {
	window.addEventListener(SOUND_CHANGED_EVENT, rebuildAudio)
}

// A media element keeps a load error permanently, so a source that failed once
// stays silent forever unless we replace the element and try again.
function playNotificationSound() {
	audio.currentTime = 0
	audio.play().catch(async () => {
		await rebuildAudio()
		audio.currentTime = 0
		audio.play().catch(err => console.warn('Alert sound failed:', err.name))
	})
}

// Variables to track title blinking state for performance and cleanup
let titleBlinkInterval = null
let titleBlinkTimeout = null
let originalTitle = null

let blinkRestoreSearchIcon = false

function startTitleBlinking(keepSearchIcon = false) {
	// Clean up any existing intervals/timeouts to prevent multiple intervals
	if (titleBlinkInterval) {
		clearInterval(titleBlinkInterval)
	}
	if (titleBlinkTimeout) {
		clearTimeout(titleBlinkTimeout)
	}

	// Store original title if not already stored (strip 🔎 indicator if present)
	if (!originalTitle) {
		originalTitle = document.title.replace(/^🔎\s*/, '')
	}

	blinkRestoreSearchIcon = keepSearchIcon

	const alertTitle = '🚨🚨🚨🚨🚨 NEW LOAD 🚨🚨🚨🚨🚨'
	let isShowingAlert = false

	// Start blinking every 1 second
	titleBlinkInterval = setInterval(() => {
		document.title = isShowingAlert ? originalTitle : alertTitle
		isShowingAlert = !isShowingAlert
	}, 500)

	// Stop blinking after 10 seconds and restore original title
	titleBlinkTimeout = setTimeout(() => {
		clearInterval(titleBlinkInterval)
		document.title = blinkRestoreSearchIcon ? `🔎 ${originalTitle}` : originalTitle
		titleBlinkInterval = null
		titleBlinkTimeout = null
	}, 12000)
}

// Cleanup function for proper memory management
export function cleanupTitleBlinking() {
	if (titleBlinkInterval) {
		clearInterval(titleBlinkInterval)
		titleBlinkInterval = null
	}
	if (titleBlinkTimeout) {
		clearTimeout(titleBlinkTimeout)
		titleBlinkTimeout = null
	}
	if (originalTitle) {
		document.title = originalTitle
		originalTitle = null
	}
}

export function isTitleBlinking() {
	return titleBlinkInterval !== null
}

export function setBlinkRestoreSearchIcon(value) {
	blinkRestoreSearchIcon = value
}

export function checkNewLoad(
	data,
	loadCache,
	isAudioOn,
	priceThreshold = 0,
	refreshSource = 'filter',
	keepSearchIcon = false
) {
	let isNewLoadDetected = false
	const processedLoads = []

	const numericPriceThreshold = Number(priceThreshold) || 0

	if (data?.loads?.length) {
		data.loads.forEach(load => {
			const cachedLoad = loadCache.get(load._id)

			if (!cachedLoad) {
				const isAutoRefresh = refreshSource === 'auto'

				loadCache.set(load._id, {
					price: load.price,
					firstSeen: Date.now(),
					originalPrice: load.price,
					highlightUntil: isAutoRefresh ? Date.now() + 60 * 1000 : null,
					newLoadMovedToTop: false,
				})

				if (isAutoRefresh) {
					processedLoads.push({
						...load,
						isNewLoad: true,
						shouldMoveToTop: true,
					})
					isNewLoadDetected = true
				} else {
					processedLoads.push(load)
				}
			} else {
				const isStillHighlighted =
					cachedLoad.highlightUntil && Date.now() < cachedLoad.highlightUntil
				const shouldMoveNewLoadToTop =
					isStillHighlighted && !cachedLoad.newLoadMovedToTop

				if (
					cachedLoad.price !== null &&
					load.price !== null &&
					load.price > cachedLoad.price
				) {
					const priceDiff = load.price - cachedLoad.price
					const originalPrice = cachedLoad.originalPrice || cachedLoad.price
					const meetsThreshold =
						numericPriceThreshold > 0 && priceDiff >= numericPriceThreshold

					const processedLoad = meetsThreshold
						? {
								...load,
								priceDifference: priceDiff,
								isPriceIncrease: true,
								shouldMoveToTop: true,
						  }
						: isStillHighlighted
						? {
								...load,
								isNewLoad: true,
								shouldMoveToTop: shouldMoveNewLoadToTop,
						  }
						: load
					processedLoads.push(processedLoad)
					if (meetsThreshold) isNewLoadDetected = true

					loadCache.set(load._id, {
						price: load.price,
						firstSeen: cachedLoad.firstSeen,
						originalPrice: originalPrice,
						highlightUntil: cachedLoad.highlightUntil,
						newLoadMovedToTop:
							cachedLoad.newLoadMovedToTop || shouldMoveNewLoadToTop,
					})
				} else if (
					cachedLoad.price !== null &&
					load.price !== null &&
					load.price < cachedLoad.price
				) {
					loadCache.set(load._id, {
						price: load.price,
						firstSeen: cachedLoad.firstSeen,
						originalPrice: cachedLoad.originalPrice || cachedLoad.price,
						highlightUntil: cachedLoad.highlightUntil,
						newLoadMovedToTop:
							cachedLoad.newLoadMovedToTop || shouldMoveNewLoadToTop,
						priceIncreaseMovedToTop: cachedLoad.priceIncreaseMovedToTop,
					})
					processedLoads.push(
						isStillHighlighted
							? {
									...load,
									isNewLoad: true,
									shouldMoveToTop: shouldMoveNewLoadToTop,
							  }
							: load
					)
				} else {
					if (shouldMoveNewLoadToTop) {
						loadCache.set(load._id, { ...cachedLoad, newLoadMovedToTop: true })
					}
					processedLoads.push(
						isStillHighlighted
							? {
									...load,
									isNewLoad: true,
									shouldMoveToTop: shouldMoveNewLoadToTop,
							  }
							: load
					)
				}
			}
		})

		if (isNewLoadDetected && isAudioOn) {
			playNotificationSound()
			startTitleBlinking(keepSearchIcon)
		}
	}

	return { loads: processedLoads, isNewLoadDetected }
}

export const queryFormatter = data => {
	const pickup = {
		lat: data.pickup.location.coordinates[1],
		lng: data.pickup.location.coordinates[0],
		maxDist: '50',
	}

	const dropOff = {
		lat: data.dropOff.location.coordinates[1],
		lng: data.dropOff.location.coordinates[0],
		maxDist: '150',
	}

	const equipments = [data?.equipment]

	const price = 0
	const pricePerMile = 0
	const stopCount = data.stopCount
	const weight = 0
	const source = []
	const paginate = { size: '100', number: '1' }
	const distance = data?.distance
	const sortBy = { lastSeen: -1 }

	const query = {
		dropOff,
		pickup,
		equipments,
		source,
		price,
		pricePerMile,
		sortBy,
		stopCount,
		weight,
		paginate,
		distance,
	}

	return query
}

export const sourcesList = [
	{ value: 'AMAZON', label: 'Amazon' },
	{ value: 'DAT', label: 'DAT' },
]

export function getOrderedLoads(loads, moveResultsToTop) {
	if (!loads || !Array.isArray(loads)) return []

	if (!moveResultsToTop) {
		return loads
	}

	const toMoveToTop = []
	const normalLoads = []

	loads.forEach(load => {
		if (load.shouldMoveToTop) {
			toMoveToTop.push(load)
		} else {
			normalLoads.push(load)
		}
	})

	return [...toMoveToTop, ...normalLoads]
}
